import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { generateTagColors } from "@remi/constants";
import { TagColorHex } from "@remi/enums/tag-color-hex";
import { isTruthy } from "@remi/ts-utils";
import { Theme } from "@remi/types/theme";

import { useTheme } from "../hooks";
import { cn } from "../utils";
import { RemiTooltip, RemiTooltipContent, RemiTooltipTrigger } from "./RemiTooltip";

const baseClass =
  "inline-flex select-none gap-x-1 items-center flex-nowrap truncate rounded-full py-0.5 font-medium tabular-nums";
const badgeVariants = cva(baseClass, {
  variants: {
    variant: {
      secondary: "border-border-primary bg-background-secondary text-text-tertiary",
      tertiary: "border-border-primary bg-background-tertiary text-text-secondary",
      destructive: "border-border-destructive bg-background-destructiveLight text-text-destructive",
      success: "border-border-success bg-background-successLight text-text-success",
      accent: "border-border-accent bg-background-accentLight text-text-accent",
      secondaryBorder:
        "border border-border-primary bg-background-secondary text-text-secondary hover:bg-background-secondaryHover",
      orange: "border-border-primary bg-avatar-orangeLight text-avatar-orangeDark",
      yellow: "border-border-primary bg-avatar-yellowLight text-avatar-yellowDark",
      green: "border-border-primary bg-avatar-greenLight text-avatar-greenDark",
      teal: "border-border-primary bg-avatar-tealLight text-avatar-tealDark",
      purple: "border-border-primary bg-avatar-purpleLight text-avatar-purpleDark",
      pink: "border-border-primary bg-avatar-pinkLight text-avatar-pinkDark",
      red: "border-border-primary bg-avatar-redLight text-avatar-redDark",
      blue: "border-border-primary bg-avatar-blueLight text-avatar-blueDark",
      grey: "border-border-primary bg-background-tertiary text-text-secondary",
    },
    size: {
      xs: "h-4 min-w-4 p-2 text-xxs",
      sm: "h-6 min-w-6 p-2 text-xs",
      md: "h-8 min-w-8 p-2 text-xs",
      lg: "h-10 min-w-10 p-3 text-sm",
    },
    border: {
      true: "border",
      false: "",
    },
  },
});

const COLOR_VARIANT_HEX = {
  orange: TagColorHex.FEF5E8,
  yellow: TagColorHex.FDF9EA,
  green: TagColorHex.EEF7EE,
  teal: TagColorHex.EAF7F9,
  purple: TagColorHex.F3EEF9,
  pink: TagColorHex.FCEEFB,
  red: TagColorHex.FEEEEE,
  blue: TagColorHex.F0FBFF,
} as const;

type ColorVariant = keyof typeof COLOR_VARIANT_HEX;

function isColorVariant(variant: string | null | undefined): variant is ColorVariant {
  return variant != null && variant in COLOR_VARIANT_HEX;
}

export interface RemiBadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  size: "xs" | "sm" | "md" | "lg";
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  border?: boolean;
  /** Renders the badge inside a tooltip trigger. A badge is not interactive, so the trigger carries
   * its own tabIndex: without one the tooltip is unreachable by keyboard. */
  toolTipContent?: React.ReactNode;
  children: React.ReactNode;
}
const RemiBadge = ({
  ref,
  className,
  variant = "secondary",
  size = "md",
  icon,
  iconPosition = "left",
  border = false,
  toolTipContent,
  children,
  style,
  ...rest
}: RemiBadgeProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const { theme } = useTheme();
  const tagColors =
    theme !== Theme.LIGHT && isColorVariant(variant) ? generateTagColors(COLOR_VARIANT_HEX[variant], true) : undefined;
  const darkColorStyle = tagColors
    ? { backgroundColor: tagColors.background, borderColor: tagColors.border, color: tagColors.text }
    : undefined;
  const badge = (
    <div
      className={cn(badgeVariants({ variant, size, border }), className)}
      style={{ ...style, ...darkColorStyle }}
      ref={ref}
      {...rest}
    >
      {isTruthy(icon) && iconPosition === "left" && (
        <span className={cn("shrink-0", children !== undefined && (size === "sm" ? "min-w-[12px]" : "min-w-[16px]"))}>
          {icon}
        </span>
      )}
      {children}
      {isTruthy(icon) && iconPosition === "right" && (
        <span className={cn("shrink-0", children !== undefined && (size === "sm" ? "min-w-[12px]" : "min-w-[16px]"))}>
          {icon}
        </span>
      )}
    </div>
  );

  if (toolTipContent === undefined) {
    return badge;
  }

  return (
    <RemiTooltip>
      <RemiTooltipTrigger asChild>
        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a badge is not interactive, so the trigger is the only focusable target the tooltip can have */}
        <span tabIndex={0}>{badge}</span>
      </RemiTooltipTrigger>
      <RemiTooltipContent className="max-w-xs">{toolTipContent}</RemiTooltipContent>
    </RemiTooltip>
  );
};
RemiBadge.displayName = "RemiBadge";

export { badgeVariants, RemiBadge };
